import { useCallback, useRef, useState, useEffect } from 'react';

import Product from './Product';
import { STATE } from '../classes/Enum';
import SearchBarComponent from './Search';
import { getInventory } from '../api/inventory';
import { getVisibleProducts, serializeItem, sortProductsByQuantity, sortProductsByPrice } from '../helper/products';
import { addToCart, getCartQuantity } from '../helper/main';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useCart } from './Context';
import { useSearch } from './Context';
import ItemInfoComponent from './ItemInfo';
import SearchFiltersComponent from './FilterComponent';
import ProductOfTheDay from './ProductOfTheDay';
import { loadDisplayOptions, saveDisplayOptions } from '../helper/displayOptions';

const POTD_INTERVAL = 10;
const IMMEDIATE_SEARCH_QUERIES = new Set(["popular", "recent"]);

function getSearchDebounceMs(queryText) {
  return IMMEDIATE_SEARCH_QUERIES.has(queryText.trim().toLowerCase()) ? 0 : 500;
}

function buildPotdSlots(productRowCount) {
  const slots = [];
  for (let i = POTD_INTERVAL; i < productRowCount; i += POTD_INTERVAL) {
    slots.push(i);
  }
  return slots;
}

function decodeVirtualRow(virtualIndex, productRowCount, potdSlots) {
  let virtual = 0;
  for (let productRowIndex = 0; productRowIndex < productRowCount; productRowIndex++) {
    if (potdSlots.includes(productRowIndex)) {
      if (virtual === virtualIndex) {
        return { type: "potd", potdIndex: potdSlots.indexOf(productRowIndex) };
      }
      virtual++;
    }
    if (virtual === virtualIndex) {
      return { type: "products", productRowIndex };
    }
    virtual++;
  }
  return { type: "products", productRowIndex: Math.max(0, productRowCount - 1) };
}

function calculateItemCardHeight(state, isMobile) {
  if (state === STATE.OVERVIEW) {
    return 150;
  } else if (state === STATE.PACK) {
    return isMobile ? 300 : 500;
  } else if (state === STATE.CARD || state === STATE.SLAB) {
    return isMobile ? 270 : 310;
  } else if (state === STATE.SEALED) {
    return isMobile ? 300 : 450;
  } else if (state === STATE.PROMO) {
    return isMobile ? 350 : 300;
  } else {
    return isMobile ? 300 : 350;
  }
}

function calculateRowHeight(state, isMobile) {
  if (state === STATE.OVERVIEW) {
    return 160;
  } else if (state === STATE.PACK) {
    return isMobile ? 350 : 550;
  } else if (state === STATE.CARD || state === STATE.SLAB) {
    return isMobile ? 400 : 420;
  } else if (state === STATE.SEALED) {
    return isMobile ? 350 : 500;
  } else if (state === STATE.PROMO) {
    return isMobile ? 410 : 450;
  }
  return 400;
}

function calculateOverlayHeight(state, isMobile, compact = false) {
  if (compact) return 0;

  if (state === STATE.OVERVIEW) {
    return 160;
  } else if (state === STATE.PACK) {
    return isMobile ? 140 : 20;
  } else if (state === STATE.CARD || state === STATE.SLAB) {
    return isMobile ? 55 : 80;
  } else if (state === STATE.SEALED) {
    return isMobile ? 40 : 0;
  } else if (state === STATE.PROMO) {
    return isMobile ? 0 : 60;
  }
  return 0;
}

const Products = ({products, addProducts, state, selectedItem, setSelectedItem, isLoading, setIsLoading,
  hoveredId, setHoveredId, isWished, setWishlist, isMobile, setIsScrolled}) => {
  const { query, setQuery, searchEventId, setSearchEventId } = useSearch();
  const [debouncedQuery, setDebouncedQuery] = useState(() => query.trim());
  const hasMore = useRef(true);
  const scrollRef = useRef(null);
  const pageRef = useRef(1);
  const queryRef = useRef(new Set());
  const fetchGenerationRef = useRef(0);
  const [searchedProducts, setSearchedProducts] = useState([]);
  const [columns, setColumns] = useState(1);
  const [activeFilters, setActiveFilters] = useState({ sort: "", series: [], set: [] });
  const [filterOptions, setFilterOptions] = useState({ series_options: [], set_options: [] });
  const [displayOptions, setDisplayOptions] = useState(loadDisplayOptions);
  const hasInitializedFilters = useRef(false);
  const skipFilterRefetch = useRef(false);
  const numOfFetches = useRef(0);
  const isSavedList = state === STATE.WISHLIST;

  const productsOnDisplay = isSavedList ? products : searchedProducts;

  let visibleproducts = debouncedQuery
    ? productsOnDisplay
    : getVisibleProducts(productsOnDisplay, debouncedQuery, state);
  const sortKey = activeFilters.sort;
  const isPriceSort = sortKey === "price_asc" || sortKey === "price_desc";

  if (isPriceSort) {
    visibleproducts = sortProductsByPrice(
      visibleproducts,
      sortKey === "price_desc" ? "desc" : "asc"
    );
  } else if (!sortKey) {
    visibleproducts = isSavedList
      ? sortProductsByQuantity([...products, ...searchedProducts])
      : sortProductsByQuantity(visibleproducts);
  }
        
  const productRowCount = Math.ceil(visibleproducts.length / columns) || 0;
  const showProductOfTheDay =
    !isSavedList &&
    !debouncedQuery &&
    visibleproducts.length > columns;
  const potdSlots =
    showProductOfTheDay && productRowCount >= POTD_INTERVAL
      ? buildPotdSlots(productRowCount)
      : [];
  const rowCount = productRowCount + potdSlots.length;
  const compactCards = displayOptions.hideProductDetails;
  const ROW_HEIGHT = calculateRowHeight(state, isMobile);
  const ITEM_CARD_HEIGHT = calculateItemCardHeight(state, isMobile);
  const OVERLAY_HEIGHT = calculateOverlayHeight(state, isMobile, compactCards);
  const POTD_ROW_HEIGHT = isMobile ? 300 : 260;
  const cardW = (state === STATE.OVERVIEW) ? 100 : (
    isMobile ? 150 : 220);
  const {cart, setCart} = useCart();
  const virtualizer = useVirtualizer({
    count: rowCount,
    estimateSize: (index) =>
      decodeVirtualRow(index, productRowCount, potdSlots).type === "potd"
        ? POTD_ROW_HEIGHT
        : ROW_HEIGHT,
    getScrollElement: () => scrollRef.current,
    overscan: 1,
    paddingStart: 300,
  });


  const virtualItems = virtualizer.getVirtualItems();

  function goToTop() {
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = 0;
      virtualizer.scrollToOffset(0);
      setIsScrolled(false);
    });
  }

  async function fetchPage() {
    if (!hasMore.current) return;
    const fetchGeneration = fetchGenerationRef.current;
    setIsLoading(true);
    try {
      let page = pageRef.current;

      let queryKey = `${state}::${debouncedQuery || ""}::${page}::${JSON.stringify(activeFilters)}`;

      if (queryRef.current.has(queryKey) && (numOfFetches.current > 0)) {
        // console.log("Skipping duplicate fetch:", queryKey);
        return;
      }

      if (queryRef.current.has(queryKey) && (numOfFetches.current === 0)) {
        pageRef.current += 1;
        page = pageRef.current;
        queryKey = `${state}::${debouncedQuery || ""}::${page}::${JSON.stringify(activeFilters)}`;
      }

      queryRef.current.add(queryKey);
      numOfFetches.current +=1;
      const { items: fetchedProducts, filterOptions: fetchedFilterOptions, searchEventId: fetchedSearchEventId } = await getInventory(
        state,
        page,
        debouncedQuery,
        activeFilters
      );
      numOfFetches.current -=1;

      if (fetchGeneration !== fetchGenerationRef.current) {
        return;
      }

      if (fetchedSearchEventId != null) {
        setSearchEventId(fetchedSearchEventId);
      }

      if (fetchedFilterOptions) {
        // Keep the fullest series/set catalog for the filter sheet. Search responses
        // used to overwrite this with a narrowed list, so changing filters after
        // confirming one + typing in search only showed partial options.
        setFilterOptions((prev) => {
          const prevSeries = prev?.series_options?.length || 0;
          const nextSeries = fetchedFilterOptions?.series_options?.length || 0;
          const prevSets = prev?.set_options?.length || 0;
          const nextSets = fetchedFilterOptions?.set_options?.length || 0;
          if (prevSeries > nextSeries || prevSets > nextSets) {
            return prev;
          }
          return fetchedFilterOptions;
        });
      }

      if (!fetchedProducts || fetchedProducts.length === 0) {
        hasMore.current = false;
        return;
      }

      addProducts(fetchedProducts, page);
      setSearchedProducts(prev => {
        const next = [...prev, ...fetchedProducts.map((p) => serializeItem(p, page))];
        const seen = new Set();
        return next.filter(p => (seen.has(p.id) ? false : (seen.add(p.id), true)));
      });
      if (page == pageRef.current) {
        pageRef.current = pageRef.current + 1;
      }

    } finally {
      if (fetchGeneration === fetchGenerationRef.current) {
        setIsLoading(false);
      }
    }
  }

  useEffect(() => {
  const el = scrollRef.current;
  if (!el) return;

  let lastScrollTop = el.scrollTop;

  const handleScroll = () => {
    const currentScrollTop = el.scrollTop;

    if (currentScrollTop > lastScrollTop) {
      // scrolling DOWN
      setIsScrolled(true);
    } else if (currentScrollTop < lastScrollTop) {
      // scrolling UP (optional)
      setIsScrolled(false);
    }

    lastScrollTop = currentScrollTop;
  };

  el.addEventListener("scroll", handleScroll);

  return () => el.removeEventListener("scroll", handleScroll);
}, []);

  useEffect(() => {
    const update = () => {
      const cols = Math.max(2, Math.floor((scrollRef.current.clientWidth) / (cardW + 50)) - 1);
      setColumns(cols);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [state, cardW]);

  
  useEffect(() => {
    virtualizer.measure();
  }, [columns, ROW_HEIGHT, virtualizer]);

  useEffect(() => {
    skipFilterRefetch.current = true;
    setActiveFilters({ sort: "", series: [], set: [] });
    setFilterOptions({ series_options: [], set_options: [] });
    setDebouncedQuery(query.trim());
  }, [state]);

  useEffect(() => {
    const trimmed = query.trim();
    const timer = setTimeout(() => {
      setDebouncedQuery((current) => {
        if (current === trimmed) return current;
        return trimmed;
      });
    }, getSearchDebounceMs(trimmed));

    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    fetchGenerationRef.current += 1;
    setSearchedProducts([]);
    hasMore.current = true;
    queryRef.current.clear();
    pageRef.current = 1;
    setSearchEventId(null);
    goToTop();
    fetchPage();
  }, [debouncedQuery, state]);

  useEffect(() => {
    if (!hasInitializedFilters.current) {
      hasInitializedFilters.current = true;
      return;
    }
    if (skipFilterRefetch.current) {
      skipFilterRefetch.current = false;
      return;
    }
    setSearchedProducts([]);
    hasMore.current = true;
    queryRef.current.clear();
    pageRef.current = 1;
    goToTop();
    fetchPage();
  }, [activeFilters]);

  useEffect(() => {
    if (!hasMore.current || (query !== debouncedQuery)) {
      return;
    }

    const THRESHOLD_ROWS = 2 * columns;
    const lastVirtualRow = virtualItems[virtualItems.length - 1];
    if (!lastVirtualRow) return;
    let lastVirtualItemIndex = lastVirtualRow.index * columns;
    // console.log(lastVirtualItemIndex, " ", productsOnDisplay.length - 1);
    if ((visibleproducts.length - 1) - lastVirtualItemIndex < THRESHOLD_ROWS) {
      // console.log("fetching from vritual item. Page: ", pageRef.current);
      fetchPage();
    } else {
      // console.log("not fetching yet.")
    }

  }, [virtualItems]);
  

  const autoAddBestCondition = (item) => {
    if (item.type === "CARD") {
      const priorityOrder = ["NM", "LP", "MP", "HP", "DMG"];

      if (!item.conditionQuantities || Object.keys(item.conditionQuantities).length === 0) return;

      const sortedVariants = Object.entries(item.conditionQuantities).sort(([a], [b]) => {
        const aIsBase = (a === "");
        const bIsBase = (b === "");
        return (bIsBase ? 1 : 0) - (aIsBase ? 1 : 0);
      });

      for (const [variant, conditions] of sortedVariants) {
        for (const cond of Object.keys(conditions)) {
          const qty = conditions?.[cond]?.quantity ?? 0;
          if (qty > 0 && (getCartQuantity(cart, item.id, variant, cond) - qty < 0)) {
            addToCart(cart, setCart, item, variant, cond);
            return;
          }
        }
      }
      return;
    }

    addToCart(cart, setCart, item, null, null);
  };

  return (
      <div className='products-container'>
              {((selectedItem !== undefined) && (selectedItem !== null)) && <ItemInfoComponent selectedItem={selectedItem} setSelectedItem={setSelectedItem} products={visibleproducts}
        getCartQuantity={getCartQuantity} isWished={isWished} setWishlist={setWishlist}/>
      }
      {([STATE.SEALED, STATE.PACK, STATE.SINGLE, STATE.SLAB, STATE.PROMO, STATE.OVERVIEW].includes(state)
       && (!selectedItem)) && <SearchBarComponent query={query} setQuery={setQuery}/>}
      {([STATE.SEALED, STATE.PACK, STATE.SINGLE, STATE.SLAB, STATE.PROMO].includes(state)
        && (!selectedItem)) && (
          <SearchFiltersComponent
            filters={activeFilters}
            onChange={setActiveFilters}
            options={filterOptions}
            displayOptions={displayOptions}
            onDisplayOptionsChange={(next) => {
              setDisplayOptions(next);
              saveDisplayOptions(next);
            }}
          />
        )}
        <div ref={scrollRef} className="virtualizer hide-scrollbar">
            {virtualItems.map(vRow => {
              const rowIndex = vRow.index;
              const decoded = decodeVirtualRow(rowIndex, productRowCount, potdSlots);

              if (decoded.type === "potd") {
                return (
                  <div
                    key={`potd-${decoded.potdIndex}-${vRow.key}`}
                    className="item-virtualizer product-of-the-day-row"
                    style={{
                      transform: `translateY(${vRow.start}px)`,
                      boxSizing: "border-box",
                      height: POTD_ROW_HEIGHT,
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <ProductOfTheDay
                      variant="inline"
                      showDivider={false}
                      slotIndex={decoded.potdIndex}
                      products={visibleproducts}
                      setSelectedItem={setSelectedItem}
                    />
                  </div>
                );
              }

              const rowItems = visibleproducts.slice(
                decoded.productRowIndex * columns,
                decoded.productRowIndex * columns + columns
              );

              return (
                <div
                  key={vRow.key}
                  className="item-virtualizer"
                  style={{
                    transform: `translateY(${vRow.start}px)`,
                    display: "grid",
                    gridTemplateColumns: `repeat(${columns}, ${cardW}px)`,
                    justifyContent: "center",
                    boxSizing: "border-box",
                    height: ITEM_CARD_HEIGHT,
                  }}
                >
                {rowItems.map(item => {
                  return (
                  <Product
                    key={item.id}
                    item={item}
                    autoAddCart={autoAddBestCondition}
                    count={getCartQuantity(cart, item.id)}
                    setSelectedItem={setSelectedItem}
                    state={state}
                    onHoverStart={() => setHoveredId(item.id)}
                    onHoverEnd={() => setHoveredId(null)}
                    isHovered={hoveredId === item.id && !!hoveredId}
                    hoveredId = {hoveredId}
                    cardW = {cardW}
                    cardH = {ROW_HEIGHT}
                    query={query}
                    isWished={isWished}
                    setWishlist={setWishlist}
                    isMobile= {isMobile}
                    overlayHeight={OVERLAY_HEIGHT}
                    cardHeight={ITEM_CARD_HEIGHT}
                    hideProductDetails={compactCards}
                  />)})}
                </div>
              );
              })
            }
          <div className="loader">
            {isLoading ? "Loading…" : ""}
          </div>
        </div>
    </div>
  );
}

export default Products;
